import { useCallback } from 'react';

import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';

export const useScanToPayTelemetry = (props: UseScanToPayTelemetryProps) => {
  const {
    isPremium,
    isOnScanAndPayTab,
    goToSgRewards,
    goToForgotToScan,
    goToSupport,
    requestStripePaymentSheet,
    switchUseCredit,
    requestLoyaltyInfo,
    requestFaq,
  } = props;

  const { track } = useTelemetry();

  const loyaltyTier = isPremium ? 'premium' : 'sg rewards';

  useTrackEventEffect({
    name: 'scan_to_pay.scan-and-pay.viewed',
    payload: { loyaltyTier },
    skip: !isOnScanAndPayTab,
  });

  useTrackEventEffect({
    name: 'scan_to_pay.scan-only.viewed',
    payload: { loyaltyTier },
    skip: isOnScanAndPayTab,
  });

  const pointsTapped = useCallback(() => {
    track('scan_to_pay.points.tapped', { loyaltyTier });
    requestLoyaltyInfo();
  }, [track, loyaltyTier, requestLoyaltyInfo]);

  const goToRewardsTapped = useCallback(() => {
    track('scan_to_pay.go-to-rewards.tapped', { loyaltyTier });
    goToSgRewards();
  }, [track, loyaltyTier, goToSgRewards]);

  const faqTapped = useCallback(() => {
    track('scan_to_pay.faq.tapped', { loyaltyTier });
    requestFaq();
  }, [track, loyaltyTier, requestFaq]);

  const contactUsTapped = useCallback(() => {
    track('scan_to_pay.contact-us.tapped', { loyaltyTier });
    goToSupport();
  }, [track, loyaltyTier, goToSupport]);

  const forgotToScanTapped = useCallback(() => {
    track('forgot-to-scan.cta_tapped', { location: 'scan_to_pay' });
    goToForgotToScan();
  }, [track, goToForgotToScan]);

  const addCardTapped = useCallback(() => {
    track('scan_to_pay.add-card.tapped', { loyaltyTier });
    requestStripePaymentSheet();
  }, [track, loyaltyTier, requestStripePaymentSheet]);

  const onSwitchUseCredit = useCallback(
    (useCredit: boolean) => {
      track(useCredit ? 'scan_to_pay.credit.enabled' : 'scan_to_pay.credit.disabled', {
        loyaltyTier,
      });

      switchUseCredit(useCredit);
    },
    [track, loyaltyTier, switchUseCredit],
  );

  const changePaymentTapped = useCallback(() => {
    track('scan_to_pay.change-payment.tapped', { loyaltyTier });
    requestStripePaymentSheet();
  }, [track, loyaltyTier, requestStripePaymentSheet]);

  return {
    pointsTapped,
    goToRewardsTapped,
    faqTapped,
    contactUsTapped,
    forgotToScanTapped,
    addCardTapped,
    onSwitchUseCredit,
    changePaymentTapped,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseScanToPayTelemetryProps = Readonly<{
  isPremium: boolean;
  isOnScanAndPayTab: boolean;
  goToSgRewards: () => void;
  goToForgotToScan: () => void;
  goToSupport: () => void;
  requestStripePaymentSheet: () => void;
  switchUseCredit: (useCredit: boolean) => void;
  requestLoyaltyInfo: () => void;
  requestFaq: () => void;
}>;
